import { z } from 'zod';
import { timestampSchema, paginationResponseSchema } from './common.schemas';

/**
 * Conversation Management Schemas
 */

// Message schema
export const messageSchema = z
  .object({
    id: z.string().uuid(),
    content: z.string(),
    role: z.enum(['user', 'assistant', 'system']),
    agentType: z
      .enum([
        'orchestrator',
        'listener',
        'resolver',
        'technical',
        'billing',
        'product',
        'anticipator',
      ])
      .nullable(),
    tokenCount: z.number().int().nonnegative(),
    cost: z.number().nonnegative(),
    sentiment: z.enum(['positive', 'neutral', 'negative', 'frustrated', 'angry']).nullable(),
    intent: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
  })
  .merge(timestampSchema);

// Conversation schema
export const conversationSchema = z
  .object({
    id: z.string().uuid(),
    customerId: z.string(),
    customerEmail: z.string().email().nullable(),
    customerName: z.string().nullable(),
    channel: z.enum(['web', 'mobile', 'email', 'slack', 'api']),
    status: z.enum(['active', 'resolved', 'escalated']),
    priority: z.enum(['low', 'normal', 'high', 'urgent']),
    sentiment: z.enum(['positive', 'neutral', 'negative', 'frustrated', 'angry']),
    effortScore: z.number().min(1).max(5).nullable(),
    resolved: z.boolean(),
    resolvedAt: z.coerce.date().nullable(),
    firstResponseAt: z.coerce.date().nullable(),
    contextSummary: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
    organizationId: z.string().uuid(),
  })
  .merge(timestampSchema);

// Conversation with messages
export const conversationWithMessagesSchema = conversationSchema.extend({
  messages: z.array(messageSchema),
});

// Create conversation
export const createConversationSchema = z.object({
  customerId: z.string().min(1),
  customerEmail: z.string().email().optional(),
  customerName: z.string().optional(),
  channel: z.enum(['web', 'mobile', 'email', 'slack', 'api']).default('web'),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
  metadata: z.record(z.unknown()).optional(),
});

// Send message
export const sendMessageSchema = z.object({
  content: z.string().min(1).max(10000),
  role: z.enum(['user', 'assistant']).default('user'),
  metadata: z.record(z.unknown()).optional(),
});

// Update conversation
export const updateConversationSchema = z.object({
  status: z.enum(['active', 'resolved', 'escalated']).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  effortScore: z.number().min(1).max(5).optional(),
  metadata: z.record(z.unknown()).optional(),
});

// Conversation list response
export const conversationListResponseSchema = z.object({
  conversations: z.array(conversationSchema),
  pagination: paginationResponseSchema,
});

// Conversation filters
export const conversationFiltersSchema = z.object({
  status: z.enum(['active', 'resolved', 'escalated']).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  channel: z.enum(['web', 'mobile', 'email', 'slack', 'api']).optional(),
  customerId: z.string().optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
});

// Export inferred types
export type Message = z.infer<typeof messageSchema>;
export type Conversation = z.infer<typeof conversationSchema>;
export type ConversationWithMessages = z.infer<typeof conversationWithMessagesSchema>;
export type CreateConversationInput = z.infer<typeof createConversationSchema>;
export type SendMessageInput = z.infer<typeof sendMessageSchema>;
export type UpdateConversationInput = z.infer<typeof updateConversationSchema>;
export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>;
export type ConversationFilters = z.infer<typeof conversationFiltersSchema>;
